import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, ArrowTabStepper, Button, Card, Spinner, useToast,
} from '@kss/ui';
import { apiClient } from '@kss/api';
import {
  ArrowLeft, ArrowRight, Banknote, Calculator, ChevronRight, FileCheck,
  Printer, Send, Users,
} from 'lucide-react';
import { printPayslips, type PayslipBlock } from './payslipPrint';

/**
 * Payroll Run wizard.
 *
 * 4-step flow:
 *   1. Period      — month/year + employee scope (active / branch / all)
 *   2. Calculate   — preview earnings/deductions/net per employee using
 *                    SalaryStructure + working-days adjustment
 *   3. Approve     — manager sign-off; freezes the calculation
 *   4. Pay         — POSTs PayrollRun + per-employee Payslip rows, then
 *                    optionally prints all payslips at once
 */

interface Employee extends Record<string, unknown> {
  employeeId?: string;
  id?: number | string;
  code?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  designation?: string;
  department?: string;
  pan?: string;
  uan?: string;
  bankAccount?: string;
  ifsc?: string;
  isActive?: number | boolean;
  branchId?: string;
}

interface SalaryStructure extends Record<string, unknown> {
  salaryStructureId?: string;
  id?: number | string;
  employeeId?: string;
  basicSalary?: number;
  hra?: number;
  conveyance?: number;
  medical?: number;
  specialAllowance?: number;
  pf?: number;
  esi?: number;
  professionalTax?: number;
  tds?: number;
  effectiveFrom?: string;
}

interface Branch extends Record<string, unknown> {
  branchId?: string;
  id?: number | string;
  name?: string;
}

interface CalcRow {
  employee: Employee;
  structure: SalaryStructure | null;
  daysInMonth: number;
  daysWorked: number;
  earnings: { name: string; amount: number }[];
  deductions: { name: string; amount: number }[];
  gross: number;
  totalDeductions: number;
  net: number;
}

const STEPS = [
  { key: 'period',    label: 'Period' },
  { key: 'calculate', label: 'Calculate' },
  { key: 'approve',   label: 'Approve' },
  { key: 'pay',       label: 'Pay' },
];

const inputCls =
  'h-9 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls =
  'h-9 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export default function PayrollRunWizard() {
  const navigate = useNavigate();
  const toast = useToast();

  // Step state
  const [step, setStep] = useState(0);
  const [savedIndices, setSavedIndices] = useState<number[]>([]);

  // Step 1 — period
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1); // 1..12
  const daysInMonth = useMemo(() => new Date(year, month, 0).getDate(), [year, month]);
  const periodLabel = `${MONTH_NAMES[month - 1]} ${year}`;

  const [scope, setScope] = useState<'all' | 'active' | 'branch'>('active');
  const [branchId, setBranchId] = useState('');
  const [defaultDaysWorked, setDefaultDaysWorked] = useState(daysInMonth);
  useEffect(() => setDefaultDaysWorked(daysInMonth), [daysInMonth]);

  // Step 2 — data + calc
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [structures, setStructures] = useState<SalaryStructure[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [calcRows, setCalcRows] = useState<CalcRow[]>([]);

  // Step 3-4
  const [posting, setPosting] = useState(false);
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      apiClient.get('/hrm/Employee').then(pickList<Employee>).catch(() => []),
      apiClient.get('/hrm/SalaryStructure').then(pickList<SalaryStructure>).catch(() => []),
      apiClient.get('/tenancy/Branch').then(pickList<Branch>).catch(() => []),
    ])
      .then(([e, s, b]) => {
        setEmployees(e);
        setStructures(s);
        setBranches(b);
      })
      .catch((e) => setErr(e instanceof Error ? e.message : 'Load failed'))
      .finally(() => setLoading(false));
  }, []);

  // Filter employees by scope.
  // §35.2 / G51: the new schema swapped `isActive: boolean` for
  // `status: number` (0=Active, 1=Passive, 2=Deleted). Old tenants may
  // still emit `isActive: 1` (numeric) too. Accept any of those shapes
  // so the "Active" scope doesn't filter out every employee just
  // because the column name flipped.
  const isEmployeeActive = (e: Employee): boolean => {
    const row = e as Record<string, unknown>;
    if (typeof row.status === 'number') return row.status === 0;
    if (typeof row.isActive === 'boolean') return row.isActive;
    if (typeof row.isActive === 'number') return row.isActive !== 0 && row.isActive !== 2;
    return true; // unknown shape → include rather than exclude
  };
  const scopedEmployees = useMemo(() => {
    return employees.filter((e) => {
      if (scope === 'active') return isEmployeeActive(e);
      if (scope === 'branch') return String((e as { branchId?: unknown }).branchId ?? '') === branchId;
      return true;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employees, scope, branchId]);

  // Build CalcRow per employee — done lazily when entering step 2.
  function calculate() {
    const structureByEmp = new Map<string, SalaryStructure>();
    for (const s of structures) {
      const id = String(s.employeeId ?? '');
      if (id) structureByEmp.set(id, s);
    }
    const rows: CalcRow[] = scopedEmployees.map((emp) => {
      const empId = String(emp.employeeId ?? emp.id ?? '');
      const struct = structureByEmp.get(empId) ?? null;
      const ratio = defaultDaysWorked / daysInMonth;
      const earnings: { name: string; amount: number }[] = [];
      const deductions: { name: string; amount: number }[] = [];
      function add(arr: typeof earnings, name: string, amt: number) {
        if (amt > 0) arr.push({ name, amount: round2(amt) });
      }
      add(earnings, 'Basic',             Number(struct?.basicSalary ?? 0) * ratio);
      add(earnings, 'HRA',               Number(struct?.hra ?? 0) * ratio);
      add(earnings, 'Conveyance',        Number(struct?.conveyance ?? 0) * ratio);
      add(earnings, 'Medical',           Number(struct?.medical ?? 0) * ratio);
      add(earnings, 'Special allowance', Number(struct?.specialAllowance ?? 0) * ratio);
      add(deductions, 'PF',                Number(struct?.pf ?? 0));
      add(deductions, 'ESI',               Number(struct?.esi ?? 0));
      add(deductions, 'Professional tax',  Number(struct?.professionalTax ?? 0));
      add(deductions, 'TDS',               Number(struct?.tds ?? 0));
      const gross = earnings.reduce((s, x) => s + x.amount, 0);
      const totalDeductions = deductions.reduce((s, x) => s + x.amount, 0);
      return {
        employee: emp,
        structure: struct,
        daysInMonth,
        daysWorked: defaultDaysWorked,
        earnings,
        deductions,
        gross: round2(gross),
        totalDeductions: round2(totalDeductions),
        net: round2(gross - totalDeductions),
      };
    });
    setCalcRows(rows);
  }

  function setRowDays(idx: number, days: number) {
    setCalcRows((cur) => {
      const row = cur[idx];
      if (!row) return cur;
      const ratio = Math.max(0, days) / row.daysInMonth;
      const earnings = row.earnings.map((e) => {
        const base = e.amount / (row.daysWorked / row.daysInMonth || 1);
        return { ...e, amount: round2(base * ratio) };
      });
      const gross = earnings.reduce((s, x) => s + x.amount, 0);
      const totalDeductions = row.totalDeductions; // statutory deductions don't pro-rate
      const next = [...cur];
      next[idx] = { ...row, daysWorked: days, earnings, gross: round2(gross), net: round2(gross - totalDeductions) };
      return next;
    });
  }

  // Aggregates for the wizard's summary blocks.
  const summary = useMemo(() => {
    const totalEmp = calcRows.length;
    const totalGross = calcRows.reduce((s, r) => s + r.gross, 0);
    const totalDeductions = calcRows.reduce((s, r) => s + r.totalDeductions, 0);
    const totalNet = calcRows.reduce((s, r) => s + r.net, 0);
    return { totalEmp, totalGross, totalDeductions, totalNet };
  }, [calcRows]);

  // Step gates
  function next() {
    if (step === 0) {
      if (scope === 'branch' && !branchId) {
        toast.error('Pick a branch', 'Branch scope is selected — choose which branch.');
        return;
      }
      calculate();
    }
    if (step === 1 && calcRows.length === 0) {
      toast.error('No employees in scope', 'Adjust the scope or seed employee data.');
      return;
    }
    setSavedIndices((p) => (p.includes(step) ? p : [...p, step]));
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  }
  function back() {
    setStep((s) => Math.max(0, s - 1));
  }

  async function postAndPay() {
    setPosting(true);
    try {
      // 1. PayrollRun header
      let runId: string | number | null = null;
      try {
        const r = await apiClient.post('/hrm/PayrollRun', {
          period: periodLabel,
          month, year,
          status: 'Paid',
          totalEmployees: summary.totalEmp,
          totalGross: round2(summary.totalGross),
          totalDeductions: round2(summary.totalDeductions),
          totalNet: round2(summary.totalNet),
          isActive: 1,
        });
        runId = (r.data?.data?.payrollRunId ?? r.data?.payrollRunId ?? r.data?.id) as string | number | null;
      } catch (e) {
        console.warn('PayrollRun post failed:', e);
      }

      // 2. Per-employee Payslip rows
      let posted = 0;
      let failed = 0;
      for (const r of calcRows) {
        try {
          await apiClient.post('/hrm/Payslip', {
            payrollRunId: runId,
            employeeId: r.employee.employeeId ?? r.employee.id,
            employeeCode: r.employee.code,
            employeeName: fullName(r.employee),
            period: periodLabel,
            month, year,
            daysWorked: r.daysWorked,
            daysInMonth: r.daysInMonth,
            grossSalary: round2(r.gross),
            totalDeductions: round2(r.totalDeductions),
            netSalary: round2(r.net),
            earnings: r.earnings,
            deductions: r.deductions,
            status: 'Issued',
          });
          posted++;
        } catch {
          failed++;
        }
      }

      if (failed === 0) {
        toast.success(
          'Payroll posted',
          `${posted} payslip(s) issued · net ₹${inr(summary.totalNet)}.`,
        );
      } else {
        toast.error(
          'Partial post',
          `${posted} succeeded · ${failed} failed. Retry from the payroll list.`,
        );
      }
      setPaid(true);
    } finally {
      setPosting(false);
    }
  }

  function printAll() {
    const slips: PayslipBlock[] = calcRows.map((r) => ({
      employeeCode: String(r.employee.code ?? r.employee.employeeId ?? r.employee.id ?? ''),
      employeeName: fullName(r.employee),
      designation: r.employee.designation,
      department: r.employee.department,
      pan: r.employee.pan,
      uan: r.employee.uan,
      bankAccount: r.employee.bankAccount,
      ifsc: r.employee.ifsc,
      period: periodLabel,
      daysWorked: r.daysWorked,
      daysInMonth: r.daysInMonth,
      earnings: r.earnings,
      deductions: r.deductions,
    }));
    printPayslips(slips, 'KSS BillBook');
  }

  return (
    <div className="-m-6 space-y-3 p-[3px]">
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-3">
        <Button variant="outline" size="sm" onClick={() => navigate('/panel/payroll/runs')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="text-base font-semibold text-content-primary">Run payroll · {periodLabel}</h1>
          <p className="text-xs text-content-muted">
            {summary.totalEmp} employee(s) · Net ₹{inr(summary.totalNet)}
          </p>
        </div>
      </div>

      {err && <Alert variant="danger">{err}</Alert>}

      <Card>
        <ArrowTabStepper
          compact
          steps={STEPS.map((s) => ({ key: s.key, label: s.label }))}
          activeIndex={step}
          savedIndices={savedIndices}
          onChange={(i) => setStep(i)}
        />

        <div className="mt-4">
          {step === 0 && (
            <div className="grid gap-3 md:grid-cols-3">
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Year</span>
                <input
                  type="number"
                  className={inputCls}
                  value={year}
                  min={2020}
                  max={2099}
                  onChange={(e) => setYear(Number(e.target.value))}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Month</span>
                <select
                  className={selectCls}
                  value={month}
                  onChange={(e) => setMonth(Number(e.target.value))}
                >
                  {MONTH_NAMES.map((m, i) => (
                    <option key={m} value={i + 1}>
                      {m}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">Days in month</span>
                <input className={inputCls} value={daysInMonth} readOnly />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="text-xs font-medium text-content-secondary">Scope</span>
                <select
                  className={selectCls}
                  value={scope}
                  onChange={(e) => setScope(e.target.value as 'all' | 'active' | 'branch')}
                >
                  <option value="active">Active employees only</option>
                  <option value="all">All employees</option>
                  <option value="branch">Specific branch</option>
                </select>
              </label>
              {scope === 'branch' && (
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium text-content-secondary">Branch</span>
                  <select
                    className={selectCls}
                    value={branchId}
                    onChange={(e) => setBranchId(e.target.value)}
                  >
                    <option value="">— pick a branch —</option>
                    {branches.map((b) => {
                      const id = String(b.branchId ?? b.id ?? '');
                      return (
                        <option key={id} value={id}>
                          {b.name ?? id}
                        </option>
                      );
                    })}
                  </select>
                </label>
              )}
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-content-secondary">
                  Default days worked
                </span>
                <input
                  type="number"
                  className={inputCls}
                  value={defaultDaysWorked}
                  min={0}
                  max={daysInMonth}
                  onChange={(e) => setDefaultDaysWorked(Number(e.target.value))}
                />
              </label>
              <div className="md:col-span-3 rounded-md border border-border bg-surface-card p-3 text-xs text-content-muted">
                <Users size={12} className="mr-1 inline" /> {scopedEmployees.length} employee(s) match the
                current scope. Click <em>Next</em> to compute their salaries from
                <code className="ml-1 rounded bg-content-muted/10 px-1">/hrm/SalaryStructure</code>.
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-3">
              {loading && calcRows.length === 0 ? (
                <div className="flex items-center gap-2 p-4 text-sm text-content-muted">
                  <Spinner /> Calculating…
                </div>
              ) : (
                <>
                  <div className="grid gap-3 sm:grid-cols-4">
                    <Stat label="Employees" value={summary.totalEmp.toLocaleString()} />
                    <Stat label="Gross" value={`₹ ${inr(summary.totalGross)}`} />
                    <Stat label="Deductions" value={`₹ ${inr(summary.totalDeductions)}`} cls="text-warning" />
                    <Stat label="Net pay" value={`₹ ${inr(summary.totalNet)}`} cls="text-success" />
                  </div>
                  <div className="overflow-x-auto rounded-md border border-border">
                    <table className="w-full text-sm">
                      <thead className="text-xs text-content-muted">
                        <tr className="border-b border-border bg-surface-card">
                          <th className="px-2 py-2 text-left">Employee</th>
                          <th className="px-2 py-2 text-right">Days worked</th>
                          <th className="px-2 py-2 text-right">Gross</th>
                          <th className="px-2 py-2 text-right">Deductions</th>
                          <th className="px-2 py-2 text-right">Net</th>
                        </tr>
                      </thead>
                      <tbody>
                        {calcRows.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="px-2 py-6 text-center text-xs text-content-muted">
                              No employees in scope. Adjust the scope and re-calculate.
                            </td>
                          </tr>
                        ) : (
                          calcRows.map((r, i) => (
                            <tr key={String(r.employee.employeeId ?? r.employee.id ?? i)} className="border-b border-border/40">
                              <td className="px-2 py-2">
                                <div className="font-medium">{fullName(r.employee)}</div>
                                <div className="text-[11px] text-content-muted">
                                  {r.employee.code ?? '—'}
                                  {!r.structure && ' · ⚠ no salary structure'}
                                </div>
                              </td>
                              <td className="px-2 py-2 text-right">
                                <input
                                  type="number"
                                  className={`${inputCls} text-right`}
                                  value={r.daysWorked}
                                  min={0}
                                  max={r.daysInMonth}
                                  onChange={(e) => setRowDays(i, Number(e.target.value))}
                                  style={{ width: 70 }}
                                />
                                <span className="ml-1 text-[10px] text-content-muted">/{r.daysInMonth}</span>
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums">{inr(r.gross)}</td>
                              <td className="px-2 py-2 text-right tabular-nums text-warning">
                                {inr(r.totalDeductions)}
                              </td>
                              <td className="px-2 py-2 text-right tabular-nums font-semibold">
                                {inr(r.net)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-3">
              <Alert variant="warning">
                Review and approve. Once approved you can&rsquo;t edit days worked from this run —
                make corrections via Payslip adjustments after posting.
              </Alert>
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                <Stat label="Period" value={periodLabel} />
                <Stat label="Employees" value={summary.totalEmp} />
                <Stat label="Gross" value={`₹ ${inr(summary.totalGross)}`} />
                <Stat label="Net pay" value={`₹ ${inr(summary.totalNet)}`} cls="text-success" />
              </div>
              <div className="rounded-md border border-border bg-surface-card p-3 text-xs">
                <div className="flex items-center gap-2 text-content-secondary">
                  <FileCheck size={14} className="text-success" />
                  <span>
                    Approver: <span className="font-medium text-content-primary">admin (TNT-0001)</span>
                  </span>
                </div>
                <div className="ml-6 mt-1 text-content-muted">
                  Approving will be audit-logged. Click <em>Next</em> to proceed to payment.
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              {paid ? (
                <Alert variant="success">
                  ✓ Payroll posted for {periodLabel}. {summary.totalEmp} payslip(s) issued.
                </Alert>
              ) : (
                <Alert variant="info">
                  Click <em>Pay &amp; issue</em> to write {summary.totalEmp} payslip row(s) and 1 payroll
                  run. You can print payslips immediately or any time later from the run detail.
                </Alert>
              )}
              <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
                <Stat label="Period" value={periodLabel} />
                <Stat label="Payslips" value={summary.totalEmp} />
                <Stat label="Net pay" value={`₹ ${inr(summary.totalNet)}`} cls="text-success" />
                <Stat label="Status" value={paid ? 'Paid' : 'Pending'} cls={paid ? 'text-success' : 'text-warning'} />
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
          <div className="text-xs text-content-muted">
            Step {step + 1} of {STEPS.length} — {STEPS[step]?.label}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => navigate('/panel/payroll/runs')} disabled={posting}>
              Cancel
            </Button>
            {step > 0 && !paid && (
              <Button variant="outline" onClick={back} disabled={posting}>
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            )}
            {step < STEPS.length - 1 && (
              <Button onClick={next} disabled={posting}>
                {step === 0 ? <Calculator className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {step === 0 ? 'Calculate' : 'Next'}
                {step !== 0 && <ArrowRight className="h-4 w-4" />}
              </Button>
            )}
            {step === STEPS.length - 1 && !paid && (
              <Button onClick={postAndPay} disabled={posting}>
                <Send className="h-4 w-4" /> {posting ? 'Posting…' : 'Pay & issue'}
              </Button>
            )}
            {step === STEPS.length - 1 && paid && (
              <>
                <Button variant="outline" onClick={printAll}>
                  <Printer className="h-4 w-4" /> Print all payslips
                </Button>
                <Button onClick={() => navigate('/panel/payroll/runs')}>
                  <Banknote className="h-4 w-4" /> Done
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value, cls }: { label: string; value: string | number; cls?: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-card px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-content-muted">{label}</div>
      <div className={`text-sm font-semibold tabular-nums ${cls ?? 'text-content-primary'}`}>{value}</div>
    </div>
  );
}

function fullName(e: Employee): string {
  if (e.fullName) return String(e.fullName);
  const f = String(e.firstName ?? '').trim();
  const l = String(e.lastName ?? '').trim();
  const full = [f, l].filter(Boolean).join(' ');
  return full || String(e.code ?? e.employeeId ?? e.id ?? 'Employee');
}

function pickList<T>(r: { data: unknown }): T[] {
  const d = r.data;
  if (Array.isArray(d)) return d as T[];
  if (d && typeof d === 'object') {
    const obj = d as { items?: unknown[]; data?: unknown[] | { items?: unknown[] } };
    if (Array.isArray(obj.items)) return obj.items as T[];
    if (Array.isArray(obj.data)) return obj.data as T[];
    if (obj.data && Array.isArray((obj.data as { items?: unknown[] }).items)) {
      return (obj.data as { items: unknown[] }).items as T[];
    }
  }
  return [];
}

function inr(n: number): string {
  if (!Number.isFinite(n)) return '0';
  return n.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
